import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Send, Loader2, ChevronRight, ArrowLeft } from 'lucide-react';
import { cn } from "@/lib/utils";

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

const DigitalPersonaOrder = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    packageInterest: '',
    projectDetails: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      const response = await fetch(`${API_URL}/api/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          services: ['Digital Persona Development', formData.packageInterest].filter(Boolean),
          projectDescription: formData.projectDetails,
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          contactMethod: 'Email'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit inquiry');
      }
      
      const result = await response.json();
      console.log('Digital persona inquiry submitted:', result);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setSubmitError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success State
  if (isSubmitted) {
    return (
      <div className="flex flex-col min-h-screen bg-[#0f1724]">
        <section className="flex-grow flex items-center justify-center px-6 py-20">
          <div className="text-center max-w-[500px]">
            <div className="w-16 h-16 rounded-full bg-[#C6A14A]/20 flex items-center justify-center mx-auto mb-6">
              <Check className="w-8 h-8 text-[#C6A14A]" />
            </div>
            <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-4">
              Commission Request Received
            </p>
            <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">
              Thank You
            </h1>
            <p className="text-white/50 leading-relaxed mb-8">
              Your private model commission request has been received. We'll review your project details and follow up within 24 hours with a custom recommendation.
            </p>
            <Link 
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#C6A14A] text-[#0f1724] font-semibold hover:bg-[#C6A14A]/90 transition-all"
            >
              Return Home
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen bg-[#0f1724]">
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0f1724] via-[#141c2b] to-[#0f1724]" />
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-15 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at center, rgba(198, 161, 74, 0.3) 0%, transparent 60%)' }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <Link 
            to="/services/digital-persona-development"
            className="inline-flex items-center gap-2 text-white/50 hover:text-[#C6A14A] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back to Digital Persona Development</span>
          </Link>
          
          <p className="text-[#C6A14A] text-xs font-semibold tracking-[0.3em] uppercase mb-3">
            Get Started
          </p>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">
            Start Your Commission
          </h1>
          <p className="text-white/50 text-base md:text-lg leading-relaxed max-w-xl mx-auto">
            Tell us about your project and we'll follow up within 24 hours with a custom recommendation.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-12 md:py-16 bg-[#0f1724]">
        <div className="max-w-xl mx-auto px-6">
          {/* Form Card */}
          <div className="bg-[#141c2b] rounded-2xl p-8 md:p-10 border border-white/5">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name Field */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Name <span className="text-[#C6A14A]">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Your name"
                  required
                  data-testid="persona-order-name-input"
                  className="w-full px-4 py-3 bg-[#0f1724] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all"
                />
              </div>

              {/* Email Field */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Email <span className="text-[#C6A14A]">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="your@email.com"
                  required
                  data-testid="persona-order-email-input"
                  className="w-full px-4 py-3 bg-[#0f1724] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all"
                />
              </div>

              {/* Phone Field */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Phone <span className="text-white/30 text-xs font-normal">(optional)</span>
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  data-testid="persona-order-phone-input"
                  className="w-full px-4 py-3 bg-[#0f1724] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all"
                />
              </div>

              {/* Package Interest */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-3">
                  Package Interest
                </label>
                <div className="grid grid-cols-1 gap-3">
                  {['Starter Expansion ($125)', 'Private LoRA Build ($350)', 'Full Ownership Package ($750+)', 'Creator Starter ($500)', 'Creator Professional ($1,200)', 'Creator Elite Digital Double ($2,500+)', 'Not sure yet'].map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => handleInputChange('packageInterest', option)}
                      data-testid={`package-option-${option.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                      className={cn(
                        "p-3 rounded-xl border text-sm font-medium transition-all duration-300 text-left",
                        formData.packageInterest === option
                          ? "border-[#C6A14A] bg-[#C6A14A]/10 text-white"
                          : "border-white/10 bg-[#0f1724] hover:border-[#C6A14A]/30 text-white/60 hover:text-white"
                      )}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              {/* Project Details Field */}
              <div>
                <label className="block text-white/70 text-sm font-medium mb-2">
                  Project Details
                </label>
                <textarea
                  value={formData.projectDetails}
                  onChange={(e) => handleInputChange('projectDetails', e.target.value)}
                  placeholder="Tell us about your intended use case, aesthetic preferences, or any questions..."
                  rows={4}
                  data-testid="persona-order-details-input"
                  className="w-full px-4 py-3 bg-[#0f1724] border border-white/10 rounded-xl text-white placeholder:text-white/30 focus:outline-none focus:border-[#C6A14A]/50 focus:ring-1 focus:ring-[#C6A14A]/20 transition-all resize-none"
                />
              </div>

              {/* Error Message */}
              {submitError && (
                <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <p className="text-red-400 text-sm">{submitError}</p>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                disabled={!formData.name || !formData.email || isSubmitting}
                data-testid="persona-order-submit-btn"
                className={cn(
                  "w-full flex items-center justify-center gap-2 py-4 rounded-xl font-semibold transition-all duration-300",
                  (!formData.name || !formData.email || isSubmitting)
                    ? "bg-white/10 text-white/30 cursor-not-allowed"
                    : "bg-[#C6A14A] text-[#0f1724] hover:bg-[#C6A14A]/90 shadow-lg hover:shadow-xl"
                )}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Submit Commission Request</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Additional Info */}
          <div className="mt-8 text-center">
            <p className="text-white/40 text-sm">
              Questions? <Link to="/contact" className="text-[#C6A14A] hover:underline">Contact us</Link> or review our{' '}
              <Link to="/services/digital-persona-development" className="text-[#C6A14A] hover:underline">service details</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* Bottom Spacer */}
      <div className="flex-grow bg-[#0f1724]" />
      <div className="h-16 bg-gradient-to-b from-[#0f1724] to-[#050816]" />
    </div>
  );
};

export default DigitalPersonaOrder;
